<template>
  <div class="space-y-6">
    <Head title="Tasas de Cambio" />

    <!-- ══ CABECERA PRINCIPAL ══ -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-2xs">
      <div>
        <div class="flex items-center gap-2.5">
          <div class="w-9 h-9 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center text-sm shadow-2xs">
            <i class="fas fa-coins text-sm" />
          </div>
          <div>
            <h1 class="text-base font-bold text-gray-900 tracking-tight leading-none">Tasas de Cambio</h1>
            <p class="text-xs text-gray-500 mt-1">Gestiona la tasa oficial del BCV o define una tasa personalizada para tus ventas y compras.</p>
          </div>
        </div>
      </div>

      <!-- Selector de cuenta (si hay más de 1 cuenta) -->
      <div v-if="accounts && accounts.length > 1" class="flex items-center gap-2">
        <label class="text-xs font-medium text-gray-500 whitespace-nowrap">Cuenta:</label>
        <select 
          v-model="selectedAccountId" 
          class="text-xs rounded-xl border-gray-200 bg-gray-50 focus:bg-white focus:ring-orange-500 focus:border-orange-500 py-1.5 pl-3 pr-8 transition-colors"
          @change="filterByAccount"
        >
          <option value="">Todas las cuentas</option>
          <option v-for="acc in accounts" :key="acc.id" :value="acc.id">{{ acc.name || acc.reasonName }}</option>
        </select>
      </div>
    </div>

    <!-- ══ TARJETAS PRINCIPALES: TASA ACTIVA vs TASA BCV ══ -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
      <!-- CARD 1: TASA ACTIVA EN EL SISTEMA -->
      <div class="bg-white rounded-2xl p-6 border border-gray-100 shadow-2xs flex flex-col justify-between relative overflow-hidden">
        <div class="flex items-start justify-between mb-4">
          <div>
            <div class="flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span class="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100">
                Tasa Activa en Sistema
              </span>
              <span 
                v-if="isCurrentRateBcv" 
                class="text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-md"
              >
                Oficial BCV
              </span>
              <span 
                v-else 
                class="text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-md"
              >
                Personalizada
              </span>
            </div>
            <p class="text-xs text-gray-400 mt-1">Esta es la tasa que se aplica actualmente en POS, ventas y cobros.</p>
          </div>

          <span class="text-xs font-medium text-gray-400">
            {{ currentTax?.account ? currentTax.account.name : 'Global' }}
          </span>
        </div>

        <div class="my-2">
          <div class="flex items-baseline gap-1.5">
            <span class="text-sm font-semibold text-gray-400">Bs.</span>
            <span class="text-4xl font-extrabold text-gray-900 tracking-tight">
              {{ formatPrice(currentTax?.change || currentTax?.rate || 0) }}
            </span>
            <span class="text-xs text-gray-400 font-medium">/ 1 USD</span>
          </div>

          <!-- Tasa COP si existe -->
          <div v-if="currentTax?.cop_change" class="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
            <span class="font-medium text-gray-400">Tasa Pesos (COP):</span>
            <span class="font-bold text-gray-700">{{ formatPrice(currentTax.cop_change) }} COP</span>
          </div>
        </div>

        <div class="pt-4 mt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
          <span>Última actualización:</span>
          <span class="font-medium text-gray-600">{{ formatDateTime(currentTax?.created_at) }}</span>
        </div>
      </div>

      <!-- CARD 2: TASA OFICIAL BCV EN TIEMPO REAL -->
      <div class="bg-gradient-to-br from-orange-50/40 via-white to-amber-50/30 rounded-2xl p-6 border border-orange-200/60 shadow-2xs flex flex-col justify-between">
        <div>
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center text-xs">
                <i class="fas fa-university" />
              </span>
              <div>
                <h3 class="text-xs font-bold text-gray-800">Tasa Oficial BCV</h3>
                <p class="text-[10px] text-gray-400">{{ liveBcvData?.source || 'Banco Central de Venezuela' }}</p>
              </div>
            </div>

            <!-- Botón Refrescar en Vivo -->
            <button 
              type="button" 
              :disabled="fetchingBcv"
              class="h-7 px-2.5 rounded-lg text-[11px] font-medium text-gray-600 hover:text-orange-600 hover:bg-orange-50 border border-gray-200/80 transition-all flex items-center gap-1.5 active:scale-95 disabled:opacity-50" 
              title="Consultar portal del BCV ahora"
              @click="refreshBcvRate"
            >
              <i class="fas fa-sync-alt text-[10px]" :class="{ 'fa-spin text-orange-500': fetchingBcv }" />
              <span>{{ fetchingBcv ? 'Consultando...' : 'Consultar BCV' }}</span>
            </button>
          </div>

          <!-- Monto BCV -->
          <div class="my-2">
            <div class="flex items-baseline gap-1.5">
              <span class="text-sm font-semibold text-orange-600/70">Bs.</span>
              <span class="text-4xl font-extrabold text-gray-900 tracking-tight">
                {{ liveBcvData?.formatted_rate || formatPrice(liveBcvData?.rate || 0) }}
              </span>
              <span class="text-xs text-gray-400 font-medium">/ 1 USD</span>
            </div>

            <!-- Detalles adicionales BCV (Fecha y Euro) -->
            <div class="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
              <span v-if="liveBcvData?.date" class="flex items-center gap-1">
                <i class="far fa-calendar-alt text-gray-400 text-[10px]" />
                <span>{{ liveBcvData.date }}</span>
              </span>
              <span v-if="liveBcvData?.euro" class="flex items-center gap-1 text-gray-400">
                <span>&bull; Euro:</span>
                <strong class="text-gray-700">Bs. {{ formatPrice(liveBcvData.euro) }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- Botón de Acción Directa para Aplicar BCV -->
        <div class="pt-4 mt-2 border-t border-orange-100/60 flex items-center justify-between gap-3">
          <span class="text-[11px] text-gray-400 font-medium">
            ¿Deseas sincronizar con esta tasa?
          </span>

          <button 
            type="button" 
            :disabled="syncingBcv || !liveBcvData?.rate"
            class="h-9 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold shadow-xs active:scale-95 transition-all flex items-center gap-1.5 disabled:opacity-50 cursor-pointer" 
            @click="applyBcvRate"
          >
            <i v-if="syncingBcv" class="fas fa-spinner fa-spin text-xs" />
            <i v-else class="fas fa-check-circle text-xs" />
            <span>{{ syncingBcv ? 'Aplicando...' : 'Aplicar Tasa BCV' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ══ SECCIÓN DE CONFIGURACIÓN / TASA PERSONALIZADA ══ -->
    <div class="bg-white rounded-2xl p-6 border border-gray-100 shadow-2xs">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-sm font-bold text-gray-900 flex items-center gap-2">
            <i class="fas fa-sliders-h text-orange-500" />
            <span>Definir Tasa Personalizada</span>
          </h2>
          <p class="text-xs text-gray-500 mt-0.5">Establece un valor a la medida si manejas una tasa propia o recargo para tu negocio.</p>
        </div>

        <button 
          type="button"
          class="h-7 px-3 text-xs font-medium rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-200 transition-colors flex items-center gap-1.5" 
          @click="showCustomForm = !showCustomForm"
        >
          <i class="fas text-[10px]" :class="showCustomForm ? 'fa-chevron-up' : 'fa-chevron-down'" />
          <span>{{ showCustomForm ? 'Ocultar formulario' : 'Abrir formulario' }}</span>
        </button>
      </div>

      <!-- Formulario de Tasa Personalizada -->
      <transition enter-active-class="transition duration-150 ease-out" enter-from-class="opacity-0 -translate-y-2" enter-to-class="opacity-100 translate-y-0">
        <form v-if="showCustomForm" class="pt-4 border-t border-gray-100" @submit.prevent="submitCustomRate">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <!-- Campo Tasa USD / Bs -->
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="text-xs font-semibold text-gray-700">Tasa USD (Bolívares) *</label>
                <button 
                  v-if="liveBcvData?.rate" 
                  type="button" 
                  class="text-[10px] text-orange-600 hover:text-orange-700 font-medium hover:underline flex items-center gap-1"
                  @click="useBcvValueInForm"
                >
                  <i class="fas fa-magic text-[9px]" /> Usar BCV ({{ formatPrice(liveBcvData.rate) }})
                </button>
              </div>
              <div class="relative">
                <span class="absolute left-3 top-2.5 text-xs text-gray-400 font-semibold">Bs.</span>
                <input 
                  v-model.number="customForm.change" 
                  type="number" 
                  step="0.0001"
                  min="0.0001"
                  required 
                  placeholder="Ej: 885.50"
                  class="w-full text-xs rounded-xl border-gray-200 pl-9 pr-3 py-2 bg-gray-50/50 focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-bold text-gray-800 transition-colors"
                  :class="{ 'border-red-300': customForm.errors.change }"
                />
              </div>
              <p v-if="customForm.errors.change" class="text-[10px] text-red-500 mt-1">{{ customForm.errors.change }}</p>
            </div>

            <!-- Campo Tasa COP (Pesos Colombianos) -->
            <div>
              <label class="text-xs font-semibold text-gray-700 block mb-1">Tasa COP (Pesos Colombianos) <span class="font-normal text-gray-400">(opcional)</span></label>
              <div class="relative">
                <span class="absolute left-3 top-2.5 text-xs text-gray-400 font-semibold">COP</span>
                <input 
                  v-model.number="customForm.cop_change" 
                  type="number" 
                  step="0.01"
                  min="0" 
                  placeholder="Ej: 4150.00"
                  class="w-full text-xs rounded-xl border-gray-200 pl-11 pr-3 py-2 bg-gray-50/50 focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold text-gray-800 transition-colors"
                />
              </div>
            </div>

            <!-- Botones de Acción -->
            <div class="flex items-center gap-2">
              <button 
                type="submit" 
                :disabled="customForm.processing || !customForm.change"
                class="h-9 px-5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-xl shadow-xs active:scale-95 transition-all flex items-center justify-center gap-1.5 flex-1 disabled:opacity-50"
              >
                <i v-if="customForm.processing" class="fas fa-spinner fa-spin text-xs" />
                <i v-else class="fas fa-save text-xs" />
                <span>{{ customForm.processing ? 'Guardando...' : 'Guardar Tasa Personalizada' }}</span>
              </button>

              <button 
                type="button" 
                class="h-9 px-3 text-xs font-medium text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200/80 rounded-xl transition-colors"
                title="Limpiar campos"
                @click="resetCustomForm"
              >
                Limpiar
              </button>
            </div>
          </div>
        </form>
      </transition>
    </div>

    <!-- ══ TABLA DE HISTORIAL DE TASAS ══ -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-2xs overflow-hidden">
      <div class="p-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 class="text-xs font-bold text-gray-800 uppercase tracking-wider">Historial de Tasas Registradas</h3>
          <p class="text-[11px] text-gray-400">Registro cronológico de todas las tasas utilizadas en el sistema.</p>
        </div>

        <span class="text-xs text-gray-400 font-medium">
          Total: {{ changes?.data ? changes.data.length : 0 }} registros
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50/80 border-b border-gray-100 text-[10px] font-bold uppercase tracking-wider text-gray-400">
              <th class="py-3 px-4">Fecha y Hora</th>
              <th class="py-3 px-4">Tasa USD (Bs.)</th>
              <th class="py-3 px-4">Tasa COP (Pesos)</th>
              <th class="py-3 px-4">Tipo / Origen</th>
              <th class="py-3 px-4">Ámbito</th>
              <th class="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 text-xs">
            <tr 
              v-for="item in (changes?.data || [])" 
              :key="item.id" 
              class="hover:bg-gray-50/60 transition-colors"
              :class="{ 'bg-orange-50/20 font-medium': item.id === currentTax?.id }"
            >
              <!-- Fecha -->
              <td class="py-3 px-4 text-gray-600">
                <div class="flex items-center gap-2">
                  <span v-if="item.id === currentTax?.id" class="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Tasa actualmente activa" />
                  <span>{{ formatDateTime(item.created_at) }}</span>
                </div>
              </td>

              <!-- Tasa USD -->
              <td class="py-3 px-4">
                <span class="font-bold text-gray-900">Bs. {{ formatPrice(item.change || item.rate) }}</span>
                <span v-if="item.id === currentTax?.id" class="ml-2 text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                  Activa
                </span>
              </td>

              <!-- Tasa COP -->
              <td class="py-3 px-4 text-gray-500">
                <span v-if="item.cop_change">{{ formatPrice(item.cop_change) }} COP</span>
                <span v-else class="text-gray-300">-</span>
              </td>

              <!-- Tipo / Origen -->
              <td class="py-3 px-4">
                <span 
                  v-if="liveBcvData?.rate && Math.abs(Number(item.change || item.rate) - Number(liveBcvData.rate)) < 0.05"
                  class="text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-md inline-flex items-center gap-1"
                >
                  <i class="fas fa-university text-[9px]" /> BCV
                </span>
                <span 
                  v-else
                  class="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md inline-flex items-center gap-1"
                >
                  <i class="fas fa-edit text-[9px]" /> Personalizada
                </span>
              </td>

              <!-- Ámbito / Cuenta -->
              <td class="py-3 px-4 text-gray-500">
                <span>{{ item.account ? item.account.name : 'Global' }}</span>
              </td>

              <!-- Acciones -->
              <td class="py-3 px-4 text-right">
                <div class="flex items-center justify-end gap-1.5">
                  <button 
                    v-if="item.id !== currentTax?.id"
                    type="button" 
                    class="h-7 px-2.5 rounded-lg text-[11px] font-medium text-orange-600 hover:bg-orange-50 border border-orange-200 transition-colors"
                    title="Reactivar esta tasa como la actual"
                    @click="reactivateRate(item)"
                  >
                    Reactivar
                  </button>
                  <Link 
                    :href="`/taxes/${item.id}/edit`" 
                    class="h-7 w-7 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors"
                    title="Editar registro"
                  >
                    <i class="fas fa-pencil-alt text-xs" />
                  </Link>
                  <button 
                    type="button" 
                    class="h-7 w-7 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors" 
                    title="Eliminar tasa"
                    @click="deleteTax(item.id)"
                  >
                    <i class="fas fa-trash text-xs" />
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="!changes?.data || changes.data.length === 0">
              <td colspan="6" class="text-center py-8 text-gray-400 italic">
                No hay registros de tasas de cambio disponibles.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="changes?.links && changes.links.length > 3" class="p-4 border-t border-gray-100 flex justify-center">
        <pagination :links="changes.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination.vue'

export default {

  components: {
    Head,
    Link,
    Pagination,
  },
  layout: Layout,

  props: {
    changes: Object,
    currentTax: Object,
    bcvData: Object,
    accounts: Array,
    filters: Object,
  },

  data() {
    return {
      selectedAccountId: this.filters?.account_id || '',
      liveBcvData: this.bcvData || null,
      fetchingBcv: false,
      syncingBcv: false,
      showCustomForm: false,
      customForm: this.$inertia.form({
        change: '',
        cop_change: this.currentTax?.cop_change || '',
        account_id: this.filters?.account_id || (this.accounts && this.accounts.length === 1 ? this.accounts[0].id : ''),
      }),
    }
  },

  computed: {
    isCurrentRateBcv() {
      if (!this.currentTax || !this.liveBcvData?.rate) return false
      const current = Number(this.currentTax.change || this.currentTax.rate)
      const bcv = Number(this.liveBcvData.rate)
      return Math.abs(current - bcv) < 0.05
    },
  },

  methods: {
    formatPrice(val) {
      if (val === null || val === undefined || isNaN(val)) return '0.00'
      return Number(val).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      try {
        const d = new Date(dateStr)
        return d.toLocaleDateString('es-VE', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (e) {
        return dateStr
      }
    },

    filterByAccount() {
      this.$inertia.get('/taxes', {
        account_id: this.selectedAccountId || undefined,
      }, {
        preserveState: true,
        preserveScroll: true,
      })
    },

    async refreshBcvRate() {
      this.fetchingBcv = true
      try {
        const resp = await fetch('/taxes/bcv-rate?force=true')
        const data = await resp.json()
        if (data && data.success) {
          this.liveBcvData = data
        } else {
          alert(data?.error || 'No se pudo consultar el BCV en este momento.')
        }
      } catch (e) {
        alert('Error de conexión al consultar el BCV.')
      } finally {
        this.fetchingBcv = false
      }
    },

    applyBcvRate() {
      if (confirm(`¿Deseas aplicar la tasa oficial del BCV (Bs. ${this.liveBcvData?.formatted_rate || this.liveBcvData?.rate}) como la tasa activa?`)) {
        this.syncingBcv = true
        this.$inertia.post('/taxes/sync-bcv', {
          account_id: this.selectedAccountId || undefined,
          cop_change: this.currentTax?.cop_change || undefined,
        }, {
          preserveScroll: true,
          onFinish: () => {
            this.syncingBcv = false
          },
        })
      }
    },

    useBcvValueInForm() {
      if (this.liveBcvData?.rate) {
        this.customForm.change = Number(this.liveBcvData.rate)
      }
    },

    resetCustomForm() {
      this.customForm.change = ''
      this.customForm.cop_change = ''
    },

    submitCustomRate() {
      this.customForm.post('/taxes', {
        preserveScroll: true,
        onSuccess: () => {
          this.showCustomForm = false
          this.resetCustomForm()
        },
      })
    },

    reactivateRate(item) {
      if (confirm(`¿Reactivar la tasa de Bs. ${this.formatPrice(item.change || item.rate)} como la activa?`)) {
        this.$inertia.post('/taxes', {
          change: Number(item.change || item.rate),
          cop_change: item.cop_change ? Number(item.cop_change) : undefined,
          account_id: item.account_id || undefined,
        }, {
          preserveScroll: true,
        })
      }
    },

    deleteTax(id) {
      if (confirm('¿Estás seguro de eliminar este registro del historial?')) {
        this.$inertia.delete(`/taxes/${id}`, {
          preserveScroll: true,
        })
      }
    },
  },
}
</script>